import { useState } from "react";
import { useLocation } from "wouter";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { UserPlus } from "lucide-react";
import { consumeReturnPath } from "@/lib/authReturnPath";

const registerSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  displayName: z.string().min(2, { message: "Display name must be at least 2 characters" }),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }),
  confirmPassword: z.string().min(6, { message: "Confirm password must be at least 6 characters" }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

type RegisterFormValues = z.infer<typeof registerSchema>;

const Register = () => {
  const { register, mode } = useAuth();
  const auth0Mode = mode === "auth0";
  const [isLoading, setIsLoading] = useState(false);
  const [_, setLocation] = useLocation();
  const { toast } = useToast();

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: "",
      displayName: "",
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: RegisterFormValues) => {
    if (auth0Mode) {
      return;
    }

    setIsLoading(true);
    try {
      await register(data.email, data.displayName, data.password, data.confirmPassword);
      toast({
        title: "Success!",
        description: "Your account has been created. You are now logged in.",
      });
      setLocation(consumeReturnPath());
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to register. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuth0Signup = async () => {
    setIsLoading(true);
    try {
      await register();
    } catch (error: any) {
      toast({
        title: "Auth0 sign-up failed",
        description:
          error?.message ||
          "We couldn't start the Auth0 sign-up flow. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full">
      {/* Left side - Auth form */}
      <div className="flex flex-1 flex-col justify-center bg-black px-8 md:px-12 py-8">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-10">
            <h1 className="text-3xl font-bold tracking-tight text-white">AIVIA</h1>
            <h2 className="mt-2 text-2xl font-semibold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Create an account
            </h2>
            <p className="mt-1 text-sm text-gray-400">
              Start planning amazing travel experiences
            </p>
          </div>
          
          <div className="space-y-5">
            <div className="flex space-x-2">
              <Button 
                variant="ghost" 
                className="flex-1 text-gray-400 hover:text-white hover:bg-gray-900"
                onClick={() => setLocation("/login")}
              >
                Login
              </Button>
              <Button 
                variant="ghost" 
                className="flex-1 text-white font-semibold bg-gray-900 hover:bg-gray-800 border-t border-gray-800"
                onClick={() => {}}
              >
                Register
              </Button>
            </div>
            
            {auth0Mode ? (
              <div className="space-y-5">
                <p className="text-gray-400">
                  Auth0 SSO is enabled. Click the button below to open the Auth0 Universal Login experience and create your account.
                </p>
                <Button
                  type="button"
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-medium py-2.5 mt-2"
                  disabled={isLoading}
                  onClick={handleAuth0Signup}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Redirecting to Auth0...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <UserPlus className="h-4 w-4" />
                      Continue with Auth0
                    </span>
                  )}
                </Button>
                <div className="mt-6 text-sm text-center text-gray-400">
                  You'll be redirected to Auth0 to finish creating your account, then
                  returned to AIVIA.
                </div>
              </div>
            ) : (
              <>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-400">Email</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="your.email@example.com"
                              className="bg-gray-900 border-gray-800 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage className="text-red-400" />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="displayName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-400">
                            Display Name
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="John Doe"
                              className="bg-gray-900 border-gray-800 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage className="text-red-400" />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-400">Password</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              placeholder="••••••••"
                              className="bg-gray-900 border-gray-800 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage className="text-red-400" />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-400">
                            Confirm Password
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              placeholder="••••••••"
                              className="bg-gray-900 border-gray-800 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage className="text-red-400" />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-medium py-2.5 mt-2"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg
                            className="animate-spin h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Registering...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <UserPlus className="h-4 w-4" />
                          Register
                        </span>
                      )}
                    </Button>
                  </form>
                </Form>

                <div className="mt-6 text-sm text-center text-gray-400">
                  By registering, you agree to AIVIA's Terms of Service and Privacy
                  Policy.
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Right side - Feature Highlights */}
      <div className="hidden lg:flex flex-1 flex-col justify-center bg-gradient-to-br from-gray-100 to-gray-200 p-12">
        <div className="mx-auto max-w-md space-y-8">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              Join our travel planning community
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Create an account to unlock the full potential of AIVIA and start building your perfect travel itineraries with AI assistance.
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-6">
            <div className="rounded-lg bg-white dark:bg-gray-800 p-5 shadow-sm">
              <h3 className="text-lg font-medium text-gray-900">AI Itineraries</h3>
              <p className="mt-2 text-sm text-gray-600">
                Generate complete travel plans with a few clicks
              </p>
            </div>
            
            <div className="rounded-lg bg-white dark:bg-gray-800 p-5 shadow-sm">
              <h3 className="text-lg font-medium text-gray-900">Trip Timeline</h3>
              <p className="mt-2 text-sm text-gray-600">
                Visualize your journey with intuitive tools
              </p>
            </div>
            
            <div className="rounded-lg bg-white dark:bg-gray-800 p-5 shadow-sm">
              <h3 className="text-lg font-medium text-gray-900">Custom Proposals</h3>
              <p className="mt-2 text-sm text-gray-600">
                Create professional travel proposals
              </p>
            </div>
            
            <div className="rounded-lg bg-white dark:bg-gray-800 p-5 shadow-sm">
              <h3 className="text-lg font-medium text-gray-900">PDF Import</h3>
              <p className="mt-2 text-sm text-gray-600">
                Extract itineraries from travel documents
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
